import { getCookie } from "helpers/cookie";
import { getLSItem, removeLSItem, setLSItem } from "util/localStorageUtils";
import PASentry from "util/pa-sentry";
import { GA4_TAG_ID } from "./env-constants";

function getCorrectGACookieValue(gaCookieValue: string) {
  return gaCookieValue.startsWith("GA") ? gaCookieValue.split(".").slice(2).join(".") : gaCookieValue;
}
let _gaCookyValue;
function getGACookie() {
  if (_gaCookyValue) {
    return _gaCookyValue;
  }
  _gaCookyValue = getCorrectGACookieValue(getCookie("_ga"));
  return _gaCookyValue;
}

// https://developers.google.com/analytics/devguides/collection/gtagjs/events
export const sendGA4Event = (action, payload) => {
  const _gaCookie = getGACookie();
  if (typeof window.gtag === "function") {
    window.gtag("event", action, {
      send_to: GA4_TAG_ID,
      gaclientid_pa: _gaCookie,
      user_id: window.customerBrowserId,
      app_version: window.version,
      ...payload,
    });
  } else {
    PASentry.logException(new Error("Gtag is not ready yet"));
  }
};

// https://support.google.com/google-ads/answer/13258081?sjid=17787937503634519984-NC#zippy=%2Cidentify-and-define-your-enhanced-conversions-fields
/*
  hashDataInBrowser is a workaround to use crypto in browser.
  if we use crypto, it will add crypto-browserify in the bundle which increases size of unused JS
*/
export async function hashDataInBrowser(data) {
  const encoder = new TextEncoder();
  const encodedData = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest("SHA-256", encodedData);
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export const setGA4UserData = async (payload: any) => {
  const userEmail = payload.email;
  const hashedUserEmail = await hashDataInBrowser(userEmail);

  window.gtag("set", "user_data", {
    sha256_email_address: hashedUserEmail,
  });
};

const getNormalizedEmail = (email: string) => {
  let normalizedEmail = email;
  if (window.redditNormalizeEmail) {
    normalizedEmail = window.redditNormalizeEmail(email);
  }
  return normalizedEmail;
};

const buildHashedUserEmailData = (hashedUserEmail: string) => ({
  user_data: {
    email_address: hashedUserEmail,
  },
});

export const getHashedUserEmail = async (email: string) => {
  const normalizedEmail = getNormalizedEmail(String(email).trim().toLowerCase());
  const hashedUserEmail = await hashDataInBrowser(normalizedEmail);
  return buildHashedUserEmailData(String(hashedUserEmail));
};

/*
  Only the hash of the email is persisted — the raw address is never kept in localStorage.
  Storing it lets the pre-GTM script in _document seed `user_data.email_address` on the very
  first page view, which is not possible from the raw email because hashing is async.
*/
export const persistHashedUserEmail = async (email: string) => {
  const hashedUserEmailObj = await getHashedUserEmail(email);
  setLSItem("emailSha", hashedUserEmailObj.user_data.email_address);
  removeLSItem("email");
  return hashedUserEmailObj;
};

/*
  Returns the persisted hashed email, or `{}` when there is none so that callers spreading
  the result never push an empty `user_data` that would clear the data layer.
  Entries written before the hash was persisted still hold a raw email; those are hashed
  once and the raw value removed, so returning users keep email matching without the
  raw address lingering in storage.
*/
export const getStoredHashedUserEmail = async (): Promise<{ user_data?: { email_address: string } }> => {
  const storedHash = getLSItem("emailSha");
  if (storedHash) {
    return buildHashedUserEmailData(storedHash);
  }
  const legacyEmail = getLSItem("email");
  if (!legacyEmail) {
    return {};
  }
  return persistHashedUserEmail(legacyEmail);
};

/*
  Pushes the hashed customerBrowserId to the data layer as an external identifier,
  so it is available for user matching (e.g. OpenAI pixel `external_id_sha256`) even
  before the user logs in. Called during the user-session refresh once the id is known.
  Sent pre-hashed (lowercase hex SHA-256) — raw external ids must never be sent.

  For a returning user whose email is already in localStorage, the hashed email is
  pushed alongside the external id so `user_data.email_address` is available from the
  start of the session — not only after an explicit login/checkout event.
*/
export const setUserDataInDataLayer = async (customerBrowserId: string) => {
  if (!customerBrowserId) {
    return;
  }
  try {
    const hashedExternalId = await hashDataInBrowser(customerBrowserId);
    const hashedUserEmailObj = await getStoredHashedUserEmail();
    window.dataLayer.push({
      user_data: {
        ...hashedUserEmailObj.user_data,
        external_id_sha256: hashedExternalId,
      },
    });
  } catch (err) {
    PASentry.logException(new Error("Failed to push external id to data layer"), null, null);
  }
};

/*
  Persists the hashed email and pushes it to the data layer so `user_data.email_address`
  is available for user matching right after an authentication. Social logins get the
  email from the login response, so this is called once that response arrives.
*/
export const pushHashedUserEmailToDataLayer = async (email: string) => {
  if (!email) {
    return;
  }
  try {
    const hashedUserEmailObj = await persistHashedUserEmail(email);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(hashedUserEmailObj);
  } catch (err) {
    PASentry.logException(new Error("Failed to push hashed user email to data layer"), null, null);
  }
};
